import type { DocumentReference as FhirDocumentReference, DocumentReferenceContent } from '@medplum/fhirtypes';
import { DocumentAttachment } from './DocumentAttachment';

/**
 * Rank an attachment `contentType` by how richly `DocumentAttachment` can present it inline.
 * Lower wins. Everything unranked (C-CDA `application/xml`, `text/rtf`, `application/octet-stream`,
 * ...) shares the bottom rank — those render as a download link, so there's no reason to prefer
 * one over another.
 */
const contentTypeRank = (contentType: string | undefined): number => {
  if (contentType?.startsWith('text/html')) return 0;
  if (contentType?.startsWith('application/pdf')) return 1;
  // Markdown ranks above plain: `DocumentAttachment` renders it formatted, plain text only verbatim.
  if (contentType?.startsWith('text/markdown')) return 2;
  if (contentType?.startsWith('text/plain')) return 3;
  if (contentType?.startsWith('image/')) return 4;
  return 5;
};

/** Whether an entry carries something `DocumentAttachment` can render (an inline `data` or a `url`). */
const hasPayload = (content: DocumentReferenceContent): boolean =>
  Boolean(content.attachment.url || content.attachment.data);

/**
 * Pick the single `content[]` entry to render. A `DocumentReference.content[]` may repeat, but each
 * repetition is the same document in a different format — FHIR R4: "There may be multiple content
 * element repetitions, each with a different format." We render the format `DocumentAttachment`
 * presents most richly and ignore the rest, so the reader never sees the same note twice.
 *
 * Entries that carry a payload are preferred over empty ones before rank is considered — otherwise a
 * higher-ranked but payload-less entry (which `DocumentAttachment` renders as `null`) would shadow a
 * renderable lower-ranked copy. Ties and entries with an unrecognized `contentType` keep document
 * order (first wins).
 */
const pickPreferredContent = (
  content: DocumentReferenceContent[] | undefined,
): DocumentReferenceContent | undefined => {
  if (!content || content.length === 0) return undefined;
  return content.reduce((best, candidate) => {
    if (hasPayload(candidate) !== hasPayload(best)) return hasPayload(candidate) ? candidate : best;
    return contentTypeRank(candidate.attachment.contentType) < contentTypeRank(best.attachment.contentType)
      ? candidate
      : best;
  });
};

export type DocumentReferenceProps = {
  documentReference: FhirDocumentReference;
  /** Iframe height in pixels (PDF and HTML modes). Defaults to 600 to match the Medplum sample. */
  height?: number;
  /** Fill the parent's height instead of using the fixed `height` (iframe modes only). */
  fill?: boolean;
};

/**
 * Render a FHIR `DocumentReference` inline. Picks the single `content[]` entry that
 * `DocumentAttachment` can present most richly (see `pickPreferredContent`) and renders only that
 * one. The chosen entry's `format.display` becomes the download-link label when the attachment is
 * untitled, e.g. a C-CDA renders as a link labeled "Structured Body C-CDA 2.1".
 *
 * Returns `null` when the resource has no usable content.
 */
export const DocumentReference = ({
  documentReference,
  height,
  fill,
}: DocumentReferenceProps): React.ReactElement | null => {
  const content = pickPreferredContent(documentReference.content);
  if (!content) return null;
  return (
    <DocumentAttachment attachment={content.attachment} label={content.format?.display} height={height} fill={fill} />
  );
};
